import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import { Child } from '../types';
import './SOCSSetupModal.css'; // Reuse SOCS modal styles

interface SchoolPortalSetupModalProps {
  isOpen: boolean;
  onClose: (completed: boolean) => void;
  children: Child[];
  suggestedEmails?: string[];
  defaultEmail?: string;
  onEmailUsed?: (email: string) => void;
  /**
   * UUIDs of children whose clubs / wrap-around care are handled by School Portal
   * (derived from the onboarding questionnaire — clubsAndExtraCurricular selection).
   * Triggers the clubs import path and shows a "must be booking parent" notice.
   */
  clubsEnabledChildUuids?: string[];
}

interface SearchResult {
  url: string;
  title: string;
  verified: boolean;
  loginUrl?: string;
}

interface SchoolPortalEvent {
  id: number;
  uuid: string;
  event_id: string;
  title: string;
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  venue: string | null;
  category: string | null;
  event_type: string | null;
  sport_name: string | null;
  team_name: string | null;
  year_groups: string[] | null;
}

/** A pupil as the school portal reports them (GET /api/students). */
interface PortalPupil {
  id: number | string;
  firstName: string;
  lastName: string;
  yearGroup: string | null;
  form: string | null;
  status: string | null;
}

const SchoolPortalSetupModal: React.FC<SchoolPortalSetupModalProps> = ({ isOpen, onClose, children, suggestedEmails = [], defaultEmail = '', onEmailUsed, clubsEnabledChildUuids = [] }) => {
  const clubsEnabledSet = new Set(clubsEnabledChildUuids);
  const hasClubsEnabledChild = children.some(c => clubsEnabledSet.has(c.uuid));
  const { user } = useAuth();
  const apiService = ApiService.getInstance();
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Wizard steps: 1 = School Search, 2 = Credentials, 3 = Fetching, 4 = Per-Child Import (one page per child)
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: School search for each child
  const [childSchoolMappings, setChildSchoolMappings] = useState<{ [childUuid: string]: string }>({});
  const [searchResults, setSearchResults] = useState<{ [childUuid: string]: SearchResult[] }>({});
  const [searching, setSearching] = useState<{ [childUuid: string]: boolean }>({});
  const [selectedSite, setSelectedSite] = useState<{ [childUuid: string]: string }>({});
  const [showSearchExpanded, setShowSearchExpanded] = useState<{ [childUuid: string]: boolean }>({});
  const [showManualUrlInput, setShowManualUrlInput] = useState<{ [childUuid: string]: boolean }>({});
  const [childManualUrls, setChildManualUrls] = useState<{ [childUuid: string]: string }>({});
  const [autoSearchTriggered, setAutoSearchTriggered] = useState<{ [childUuid: string]: boolean }>({});

  // Step 2: Credentials per school
  const [credentials, setCredentials] = useState<{ [schoolUrl: string]: { email: string; password: string } }>({});
  const [showPassword, setShowPassword] = useState<{ [schoolUrl: string]: boolean }>({});
  const [showCustomEmailInput, setShowCustomEmailInput] = useState<{ [schoolUrl: string]: boolean }>({});

  // Step 4: Per-child import state
  const [currentChildIndex, setCurrentChildIndex] = useState(0);
  const [childrenWithSchools, setChildrenWithSchools] = useState<Child[]>([]);

  // Multi-select year groups per child
  const [selectedYearGroups, setSelectedYearGroups] = useState<{ [childUuid: string]: Set<string> }>({});
  const [excludeSportFixtures, setExcludeSportFixtures] = useState<{ [childUuid: string]: boolean }>({});

  // Fetched events and year groups per school
  const [siteUuidPerSchool, setSiteUuidPerSchool] = useState<{ [schoolUrl: string]: string }>({});
  const [fetchedEventsPerSchool, setFetchedEventsPerSchool] = useState<{ [schoolUrl: string]: SchoolPortalEvent[] }>({});
  const [availableYearGroupsPerSchool, setAvailableYearGroupsPerSchool] = useState<{ [schoolUrl: string]: string[] }>({});

  // Pupils as the school portal reports them, including their CURRENT year group.
  // The parent no longer picks a year group - the portal is the source of truth,
  // and the sync job re-reads it so it stays right after the September rollover.
  const [portalPupilsPerSchool, setPortalPupilsPerSchool] = useState<{ [schoolUrl: string]: PortalPupil[] }>({});

  // Import status per child
  const [childImportStatus, setChildImportStatus] = useState<{ [childUuid: string]: { imported: number; skipped: number } }>({});
  const [importing, setImporting] = useState(false);
  // Non-null while the booked-bundles (clubs) sync is running for a child. The
  // step holds here: the term planner is built from what this fetches.
  const [syncingClubsFor, setSyncingClubsFor] = useState<string | null>(null);

  // Sync progress status for user feedback
  const [syncStatus, setSyncStatus] = useState<string>('');
  const [retryStatus, setRetryStatus] = useState<string>('');
  const [isRetrying, setIsRetrying] = useState(false);

  // Track which schools are still being fetched in background
  const [schoolsBeingFetched, setSchoolsBeingFetched] = useState<Set<string>>(new Set());
  const schoolsBeingFetchedRef = useRef<Set<string>>(new Set());
  // Track which schools failed to fetch (for retry functionality)
  const [schoolsFetchFailed, setSchoolsFetchFailed] = useState<Set<string>>(new Set());

  // Deselected events per child (for individual event checkboxes) - using UUIDs
  const [deselectedEvents, setDeselectedEvents] = useState<{ [childUuid: string]: Set<string> }>({});

  // Track if mousedown started inside modal
  const mouseDownInsideModal = useRef(false);

  // Track previous isOpen state to detect fresh open vs resume
  const wasOpen = useRef(false);

  useEffect(() => {
    if (isOpen) {
      // Only reset state on FRESH open (was closed, now open)
      // Don't reset on resume from background (was open, still open)
      if (!wasOpen.current) {
        loadExistingMappings();
        setStep(1);
        setError(null);
        setCurrentChildIndex(0);
        setChildImportStatus({});
        setSyncStatus('');
        setRetryStatus('');
        setIsRetrying(false);
      }
      wasOpen.current = true;
    } else {
      wasOpen.current = false;
    }
  }, [isOpen, children]);

  const loadExistingMappings = async () => {
    setLoading(true);
    try {
      const initialMappings: { [key: string]: string } = {};
      const initialCredentials: { [key: string]: { email: string; password: string } } = {};
      const autoSearchFlags: { [key: string]: boolean } = {};
      const childrenToAutoSearch: string[] = [];

      for (const child of children) {
        // Initialize to false - will be set to true when auto-search is triggered
        autoSearchFlags[child.uuid] = false;

        try {
          const response = await apiService.schoolPortalGetChildMappings(child.uuid);
          if (response.success && response.data?.mapping) {
            const mapping = response.data.mapping;
            initialMappings[child.uuid] = mapping.school_name || child.school_venue || child.school || '';

            if (mapping.school_url) {
              setSelectedSite(prev => ({ ...prev, [child.uuid]: mapping.school_url }));
              setChildManualUrls(prev => ({ ...prev, [child.uuid]: mapping.school_url }));
              setSearchResults(prev => ({
                ...prev,
                [child.uuid]: [{
                  url: mapping.school_url,
                  title: `${mapping.school_name} School Portal`,
                  verified: true,
                  loginUrl: mapping.login_url
                }]
              }));
              // Already have a site selected, mark as triggered so useEffect doesn't auto-search
              autoSearchFlags[child.uuid] = true;

              if (mapping.email) {
                initialCredentials[mapping.school_url] = {
                  email: mapping.email,
                  password: ''
                };
              }
            }
            // If no school_url but has school name, leave autoSearchFlags[child.uuid] = false
            // so the useEffect will trigger auto-search
          } else {
            initialMappings[child.uuid] = child.school_venue || child.school || '';
            // Leave autoSearchFlags[child.uuid] = false so useEffect will trigger auto-search
          }
        } catch (e) {
          initialMappings[child.uuid] = child.school_venue || child.school || '';
          // Leave autoSearchFlags[child.uuid] = false so useEffect will trigger auto-search
        }
      }

      setChildSchoolMappings(initialMappings);
      setCredentials(initialCredentials);
      setAutoSearchTriggered(autoSearchFlags);
      // Auto-search is now handled by useEffect that watches for state changes
    } catch (error) {
      console.error('Error loading existing mappings:', error);
      const initialMappings: { [key: string]: string } = {};
      const autoSearchFlags: { [key: string]: boolean } = {};
      children.forEach(child => {
        initialMappings[child.uuid] = child.school_venue || child.school || '';
        // Set to false so useEffect will trigger auto-search if school name exists
        autoSearchFlags[child.uuid] = false;
      });
      setChildSchoolMappings(initialMappings);
      setAutoSearchTriggered(autoSearchFlags);
      // Auto-search is now handled by useEffect that watches for state changes
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [step, currentChildIndex, isOpen]);

  // Auto-trigger search when modal opens with pre-populated school data
  // This useEffect watches for state changes and triggers search when ready
  useEffect(() => {
    if (isOpen && step === 1 && !loading) {
      children.forEach((child) => {
        const schoolName = childSchoolMappings[child.uuid]?.trim();
        // Only auto-search if:
        // 1. There's a school name from existing data
        // 2. We haven't already triggered auto-search for this child
        // 3. There are no existing search results
        // 4. Not currently searching
        // 5. No site already selected
        if (
          schoolName &&
          autoSearchTriggered[child.uuid] === false &&
          (!searchResults[child.uuid] || searchResults[child.uuid].length === 0) &&
          !searching[child.uuid] &&
          !selectedSite[child.uuid]
        ) {
          setAutoSearchTriggered(prev => ({ ...prev, [child.uuid]: true }));
          handleSearchSchoolSite(child.uuid);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, loading, children, childSchoolMappings, autoSearchTriggered]);

  const handleSchoolNameChange = (childUuid: string, schoolName: string) => {
    setChildSchoolMappings(prev => ({ ...prev, [childUuid]: schoolName }));
  };

  const handleManualUrlChange = (childUuid: string, manualUrl: string) => {
    setChildManualUrls(prev => ({ ...prev, [childUuid]: manualUrl }));
  };

  const handleSearchSchoolSite = async (childUuid: string) => {
    const schoolName = childSchoolMappings[childUuid]?.trim();
    if (!schoolName) {
      setError('Please enter a school name first');
      return;
    }

    setSearchResults(prev => ({ ...prev, [childUuid]: [] }));
    setSelectedSite(prev => ({ ...prev, [childUuid]: '' }));
    setShowManualUrlInput(prev => ({ ...prev, [childUuid]: false }));
    setSearching(prev => ({ ...prev, [childUuid]: true }));
    setError(null);

    try {
      const response = await apiService.schoolPortalSearchSchoolSite(schoolName);

      if (response.success && response.data?.sites) {
        let verifiedSites = response.data.sites.filter((site: SearchResult) => site.verified);

        const httpsUrls = verifiedSites
          .filter((site: SearchResult) => site.url.startsWith('https://'))
          .map((site: SearchResult) => site.url.replace('https://', ''));
        verifiedSites = verifiedSites.filter((site: SearchResult) => {
          if (site.url.startsWith('http://')) {
            const httpHost = site.url.replace('http://', '');
            return !httpsUrls.includes(httpHost);
          }
          return true;
        });

        setSearchResults(prev => ({ ...prev, [childUuid]: verifiedSites }));

        if (verifiedSites.length === 1) {
          setSelectedSite(prev => ({ ...prev, [childUuid]: verifiedSites[0].url }));
          setChildManualUrls(prev => ({ ...prev, [childUuid]: verifiedSites[0].url }));
        }

        if (verifiedSites.length === 0) {
          setShowManualUrlInput(prev => ({ ...prev, [childUuid]: true }));
          setChildManualUrls(prev => ({ ...prev, [childUuid]: 'https://parents.' }));
          setError(response.error || 'No My School Portal websites found. Please enter the URL manually.');
        }
      } else {
        setShowManualUrlInput(prev => ({ ...prev, [childUuid]: true }));
        setChildManualUrls(prev => ({ ...prev, [childUuid]: 'https://parents.' }));
        setError(response.error || 'No My School Portal websites found. Please enter the URL manually.');
      }
    } catch (err: any) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search for School Portal sites');
      setShowManualUrlInput(prev => ({ ...prev, [childUuid]: true }));
      setChildManualUrls(prev => ({ ...prev, [childUuid]: 'https://parents.' }));
    } finally {
      setSearching(prev => ({ ...prev, [childUuid]: false }));
    }
  };

  const handleSelectSite = (childUuid: string, siteUrl: string) => {
    setSelectedSite(prev => ({ ...prev, [childUuid]: siteUrl }));
    setChildManualUrls(prev => ({ ...prev, [childUuid]: siteUrl }));
  };

  const handleStep1Next = () => {
    const uniqueSchoolUrls = new Set<string>();
    children.forEach(child => {
      const url = childManualUrls[child.uuid]?.trim();
      if (url && url !== 'https://parents.' && url !== '') {
        uniqueSchoolUrls.add(url);
      }
    });

    if (uniqueSchoolUrls.size === 0) {
      setError('Please select or enter at least one school portal URL');
      return;
    }

    const newCredentials = { ...credentials };
    uniqueSchoolUrls.forEach(url => {
      if (!newCredentials[url]) {
        // Pre-populate with default email if available
        newCredentials[url] = { email: defaultEmail || '', password: '' };
      }
    });
    setCredentials(newCredentials);

    setStep(2);
    setError(null);
  };

  const handleCredentialChange = (schoolUrl: string, field: 'email' | 'password', value: string) => {
    setCredentials(prev => ({
      ...prev,
      [schoolUrl]: { ...prev[schoolUrl], [field]: value }
    }));
  };

  // Helper to process fetch response and extract year groups
  const processSchoolFetchResponse = (schoolUrl: string, fetchResponse: any) => {
    const yearGroupPatterns = [
      /year\s*\d+/i, /^y\d+$/i, /reception/i, /nursery/i, /pre-?prep/i,
      /prep/i, /sixth\s*form/i, /lower\s*school/i, /upper\s*school/i,
      /senior/i, /junior/i, /infant/i,
    ];

    const events: SchoolPortalEvent[] = fetchResponse.data?.events || [];
    const siteUuid = fetchResponse.data?.siteUuid || '';

    const yearGroupsSet = new Set<string>();
    events.forEach(event => {
      let foundInCategory = false;
      if (event.category) {
        event.category.split(',').forEach(cat => {
          const trimmed = cat.trim();
          if (trimmed && yearGroupPatterns.some(pattern => pattern.test(trimmed))) {
            yearGroupsSet.add(trimmed);
            foundInCategory = true;
          }
        });
      }
      if (!foundInCategory && event.year_groups && Array.isArray(event.year_groups)) {
        event.year_groups.forEach(yg => {
          if (yg) {
            yearGroupsSet.add(yg);
          }
        });
      }
    });

    // Sort year groups
    const sortedYearGroups = Array.from(yearGroupsSet).sort((a, b) => {
      const getYearNumber = (s: string): number | null => {
        const match = s.match(/^(?:y|year\s*)(\d+)$/i);
        return match ? parseInt(match[1]) : null;
      };
      const numA = getYearNumber(a);
      const numB = getYearNumber(b);
      if (numA !== null && numB !== null) return numA - numB;
      if (numA !== null) return -1;
      if (numB !== null) return 1;
      return a.localeCompare(b);
    });

    const pupils: PortalPupil[] = fetchResponse.data?.pupils || [];

    return { events, siteUuid, yearGroups: sortedYearGroups, pupils };
  };

  /**
   * The year group the school portal says this child is currently in.
   * Matched on the portal's own pupil records, by first name.
   */
  const detectedYearGroupFor = (child: Child): string | null => {
    const schoolUrl = childManualUrls[child.uuid]?.trim();
    if (!schoolUrl) return null;

    const pupils = portalPupilsPerSchool[schoolUrl] || [];
    if (pupils.length === 0) return null;

    const first = (child.name || '').toLowerCase().trim().split(/\s+/)[0] || '';
    const match = pupils.find(p =>
      first && `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase().includes(first)
    ) || (pupils.length === 1 ? pupils[0] : null);

    return match?.yearGroup || null;
  };

  const handleStep2Next = async () => {
    const uniqueSchoolUrls = Object.keys(credentials);
    for (const url of uniqueSchoolUrls) {
      const cred = credentials[url];
      if (!cred.email || !cred.password) {
        setError(`Please enter email and password for ${url}`);
        return;
      }
    }

    setLoading(true);
    setError(null);
    setSyncStatus('Preparing to connect...');
    setStep(3);

    try {
      // Set up children with schools
      const childrenWithUrls = children.filter(child => childManualUrls[child.uuid]?.trim());
      setChildrenWithSchools(childrenWithUrls);

      // Initialize default selections for all children upfront
      const defaultYearGroups: { [key: string]: Set<string> } = {};
      const defaultExcludeSport: { [key: string]: boolean } = {};
      const defaultDeselected: { [key: string]: Set<string> } = {};
      childrenWithUrls.forEach(child => {
        defaultYearGroups[child.uuid] = new Set();
        defaultExcludeSport[child.uuid] = true;
        defaultDeselected[child.uuid] = new Set();
      });
      setSelectedYearGroups(defaultYearGroups);
      setExcludeSportFixtures(defaultExcludeSport);
      setDeselectedEvents(defaultDeselected);

      // Process schools SEQUENTIALLY to avoid concurrent Chromium launches
      // (Heroku can't handle multiple browser instances launching at once)
      // BUT show results to user after first school completes
      const schoolUrls = Array.from(new Set(Object.keys(credentials)));

      // Mark all schools except the first as "being fetched" so we can show loading state
      if (schoolUrls.length > 1) {
        const pending = new Set(schoolUrls.slice(1));
        setSchoolsBeingFetched(pending);
        schoolsBeingFetchedRef.current = new Set(pending);
      }

      // Helper function to fetch a single school's events
      const fetchSchoolEvents = async (schoolUrl: string, showStatus: boolean = true): Promise<boolean> => {
        const cred = credentials[schoolUrl];
        const childrenForSchool = children.filter(child =>
          childManualUrls[child.uuid]?.trim() === schoolUrl
        );
        const schoolDomain = schoolUrl.replace('https://', '').replace('http://', '').split('/')[0];

        try {
          // Sync credentials first
          if (showStatus) setSyncStatus(`Attempting login to ${schoolDomain}...`);
          await apiService.schoolPortalSync(
            schoolUrl, cred.email, cred.password,
            childrenForSchool.map(c => c.uuid), {}, true
          );

          // Fetch events - only future events (from today to 1 year ahead)
          if (showStatus) setSyncStatus(`Login successful! Fetching calendar events from ${schoolDomain}...`);
          const today = new Date();
          const oneYearAhead = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
          const fromDate = today.toISOString().split('T')[0];
          const toDate = oneYearAhead.toISOString().split('T')[0];

          const fetchResponse = await apiService.schoolPortalFetchEvents(
            schoolUrl, cred.email, cred.password, fromDate, toDate
          );

          if (fetchResponse.success) {
            if (showStatus) setSyncStatus(`Processing ${schoolDomain} calendar data...`);
            const { events, siteUuid, yearGroups, pupils } = processSchoolFetchResponse(schoolUrl, fetchResponse);

            // Check if we got any events - if not, mark as failed
            if (events.length === 0) {
              console.warn(`No events returned for ${schoolUrl}`);
              if (showStatus) setSyncStatus(`No events found for ${schoolDomain}`);
              setSchoolsFetchFailed(prev => new Set(prev).add(schoolUrl));
              return false;
            }

            if (showStatus) setSyncStatus(`Found ${events.length} events from ${schoolDomain}!`);

            // Update state with this school's data
            setSiteUuidPerSchool(prev => ({ ...prev, [schoolUrl]: siteUuid }));
            setFetchedEventsPerSchool(prev => ({ ...prev, [schoolUrl]: events }));
            setAvailableYearGroupsPerSchool(prev => ({ ...prev, [schoolUrl]: yearGroups }));
            setPortalPupilsPerSchool(prev => ({ ...prev, [schoolUrl]: pupils }));

            // Clear any previous failure state
            setSchoolsFetchFailed(prev => {
              const next = new Set(prev);
              next.delete(schoolUrl);
              return next;
            });
            return true;
          } else {
            console.error(`Fetch failed for ${schoolUrl}:`, fetchResponse.error);
            if (showStatus) setSyncStatus(`Failed to fetch events from ${schoolDomain}`);
            setSchoolsFetchFailed(prev => new Set(prev).add(schoolUrl));
            return false;
          }
        } catch (err: any) {
          console.error(`Error fetching ${schoolUrl}:`, err);
          const errorMsg = err?.message || '';
          if (errorMsg.includes('PORTAL_DISABLED')) {
            if (showStatus) setSyncStatus(`${schoolDomain}: Portal disabled by the school — please contact them`);
          } else if (errorMsg.includes('Login failed') || errorMsg.includes('authentication')) {
            if (showStatus) setSyncStatus(`Login failed for ${schoolDomain} - check credentials`);
          } else {
            if (showStatus) setSyncStatus(`Connection error with ${schoolDomain}`);
          }
          setSchoolsFetchFailed(prev => new Set(prev).add(schoolUrl));
          return false;
        } finally {
          // Mark this school as done fetching (whether success or failure)
          setSchoolsBeingFetched(prev => {
            const next = new Set(prev);
            next.delete(schoolUrl);
            return next;
          });
          schoolsBeingFetchedRef.current.delete(schoolUrl);
        }
      };

      // Fetch first school and show UI immediately when done
      if (schoolUrls.length > 0) {
        try {
          await fetchSchoolEvents(schoolUrls[0]);
        } catch (err: any) {
          console.error(`Failed to fetch events for ${schoolUrls[0]}:`, err);
        }

        // Show UI immediately after first school completes
        setCurrentChildIndex(0);
        setStep(4);
        setLoading(false);

        // Notify about custom emails used (for reuse in other integrations)
        if (onEmailUsed) {
          Object.values(credentials).forEach(cred => {
            if (cred.email && !suggestedEmails.includes(cred.email)) {
              onEmailUsed(cred.email);
            }
          });
        }

        // Fetch remaining schools in background (non-blocking)
        // Use setTimeout to allow React to render before starting next fetch
        if (schoolUrls.length > 1) {
          setTimeout(async () => {
            for (let i = 1; i < schoolUrls.length; i++) {
              try {
                await fetchSchoolEvents(schoolUrls[i]);
              } catch (err: any) {
                console.error(`Failed to fetch events for ${schoolUrls[i]}:`, err);
                // Still mark as done even on error so loading state clears
                setSchoolsBeingFetched(prev => {
                  const next = new Set(prev);
                  next.delete(schoolUrls[i]);
                  return next;
                });
                schoolsBeingFetchedRef.current.delete(schoolUrls[i]);
              }
            }
          }, 100);
        }
      } else {
        // No schools to process
        setCurrentChildIndex(0);
        setStep(4);
        setLoading(false);
      }

    } catch (err: any) {
      setError(err.message || 'Failed to fetch events');
      setLoading(false);
    }
  };

  // Get filtered events for current child
  const getFilteredEventsForChild = (child: Child): SchoolPortalEvent[] => {
    const schoolUrl = childManualUrls[child.uuid]?.trim();
    if (!schoolUrl) return [];

    let events = fetchedEventsPerSchool[schoolUrl] || [];

    // Prefer the portal's own year group for this pupil; fall back to a manual
    // selection only where the portal could not tell us (older portals).
    const detected = detectedYearGroupFor(child);
    const selectedYGs = detected
      ? new Set([detected])
      : (selectedYearGroups[child.uuid] || new Set());

    // Apply year group filter if any selected
    if (selectedYGs.size > 0) {
      events = events.filter(e => {
        // ECAs are child-specific activities (like drum lessons) - always include them
        // regardless of year group filter since they're personalized to this child
        if (e.event_type === 'ECA') return true;

        // DEBUG: Log Wicked event filtering
        if (e.title?.toLowerCase().includes('wicked')) {
          console.log('🎭 WICKED EVENT FILTER DEBUG:', {
            title: e.title,
            category: e.category,
            year_groups: e.year_groups,
            selectedYGs: Array.from(selectedYGs)
          });
        }
        // Check category field (authoritative source)
        if (e.category) {
          const cats = e.category.split(',').map(c => c.trim());
          const categoryMatch = cats.some(c => selectedYGs.has(c));
          if (e.title?.toLowerCase().includes('wicked')) {
            console.log('🎭 WICKED category check:', { cats, categoryMatch });
          }
          if (categoryMatch) return true;
        }
        // Check year_groups array
        if (e.year_groups && Array.isArray(e.year_groups)) {
          // Only include "All Years" events if no specific year groups are in the event
          // This prevents events like "Russell House Theatre Trip" with specific years from showing to everyone
          const hasSpecificYearGroups = e.year_groups.some(yg =>
            yg !== 'All Years' && (
              /^y(ear)?\s*\d+$/i.test(yg) ||
              /^reception$/i.test(yg) ||
              /^nursery$/i.test(yg) ||
              /^prep$/i.test(yg) ||
              /^pre-?prep$/i.test(yg) ||
              /^senior/i.test(yg) ||
              /^heatherton/i.test(yg)
            )
          );

          if (e.title?.toLowerCase().includes('wicked')) {
            console.log('🎭 WICKED year_groups check:', {
              year_groups: e.year_groups,
              hasSpecificYearGroups,
              includesAllYears: e.year_groups.includes('All Years'),
              willPassAllYearsCheck: !hasSpecificYearGroups && e.year_groups.includes('All Years')
            });
          }

          // If event has only "All Years" (no specific groups), include it
          if (!hasSpecificYearGroups && e.year_groups.includes('All Years')) return true;

          // Match specific year groups
          if (e.year_groups.some(yg => selectedYGs.has(yg))) return true;
        }
        return false;
      });
    }

    // Apply sport fixture filter
    if (excludeSportFixtures[child.uuid]) {
      events = events.filter(e => e.event_type !== 'Fixture');
    }

    // Filter to future events only (from today)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    events = events.filter(e => {
      if (!e.event_date) return false;
      const eventDate = new Date(e.event_date);
      return eventDate >= today;
    });

    return events;
  };

  // Toggle year group selection
  const toggleYearGroup = (childUuid: string, yearGroup: string) => {
    setSelectedYearGroups(prev => {
      const current = new Set(prev[childUuid] || []);
      if (current.has(yearGroup)) {
        current.delete(yearGroup);
      } else {
        current.add(yearGroup);
      }
      return { ...prev, [childUuid]: current };
    });
  };

  // Toggle event selection for individual event deselection - using UUIDs
  const toggleEventSelection = (childUuid: string, eventUuid: string) => {
    setDeselectedEvents(prev => {
      const current = new Set(prev[childUuid] || []);
      if (current.has(eventUuid)) {
        current.delete(eventUuid);
      } else {
        current.add(eventUuid);
      }
      return { ...prev, [childUuid]: current };
    });
  };

  // Get events to import (filtered minus deselected) - using UUIDs
  const getEventsToImport = (child: Child): SchoolPortalEvent[] => {
    const filteredEvents = getFilteredEventsForChild(child);
    const deselected = deselectedEvents[child.uuid] || new Set();
    return filteredEvents.filter(e => !deselected.has(e.uuid));
  };

  // Import events for current child
  const handleImportForChild = async () => {
    const child = childrenWithSchools[currentChildIndex];
    if (!child) return;

    const schoolUrl = childManualUrls[child.uuid]?.trim();
    const siteUuid = siteUuidPerSchool[schoolUrl];
    if (!siteUuid) {
      setError('No site found for this school');
      return;
    }

    // Use getEventsToImport which excludes deselected events
    const events = getEventsToImport(child);
    const childWantsClubsEarly = clubsEnabledSet.has(child.uuid);
    if (events.length === 0 && !childWantsClubsEarly) {
      // No events to import and no clubs needed — move to next child
      moveToNextChild();
      return;
    }

    setImporting(true);
    setError(null);

    // Get credentials for this school
    const cred = credentials[schoolUrl];
    if (!cred) {
      setError('Credentials not found for this school');
      setImporting(false);
      return;
    }

    try {
      // Get filter preferences to save for auto-sync
      // The portal's year group wins. The sync job re-reads it on every run, so
      // this only needs to be right enough to seed the first import.
      const detectedYearGroup = detectedYearGroupFor(child);
      const yearGroupsSet = selectedYearGroups[child.uuid] || new Set();
      const yearGroupsArray = detectedYearGroup
        ? [detectedYearGroup]
        : Array.from(yearGroupsSet);
      const excludeFixtures = excludeSportFixtures[child.uuid] || false;

      const childWantsClubs = clubsEnabledSet.has(child.uuid);

      apiService.schoolPortalImportEvents(
        siteUuid,
        events.map(e => e.uuid),
        child.uuid,
        undefined,
        schoolUrl,
        cred.email,
        cred.password,
        yearGroupsArray,
        excludeFixtures,
        childWantsClubs
      ).then(importResponse => {
        if (!importResponse.success) {
          console.warn(`[SP-IMPORT] Failed for ${child.uuid}:`, importResponse.error);
        }
      }).catch(err => {
        console.warn(`[SP-IMPORT] Error for ${child.uuid}:`, err?.message || err);
      });

      // Show optimistic counts so the UI can advance immediately
      setChildImportStatus(prev => ({
        ...prev,
        [child.uuid]: {
          imported: events.length,
          skipped: 0
        }
      }));

      if (childWantsClubs) {
        // Await this. The clubs sync is what populates club_data_cache, and the
        // term planner is built from that cache — advancing while it is still
        // running is how a planner ends up empty. Events (above) stay
        // fire-and-forget: they write activities directly and nothing downstream
        // waits on them.
        setSyncingClubsFor(child.first_name || 'your child');
        try {
          const clubsResponse = await apiService.schoolPortalSyncClubs(child.uuid);
          if (!clubsResponse.success) {
            console.warn(`[SP-CLUBS] Sync failed for ${child.uuid}:`, clubsResponse.error);
            setError(
              `We saved your School Portal login but could not import ${child.first_name}'s clubs: ` +
              `${clubsResponse.error || 'the sync failed'}. Please try again — the term planner is built from this data.`
            );
            return;
          }
        } catch (clubsErr: any) {
          console.warn(`[SP-CLUBS] Sync error for ${child.uuid}:`, clubsErr?.message || clubsErr);
          setError(
            `We saved your School Portal login but could not import ${child.first_name}'s clubs: ` +
            `${clubsErr?.message || 'the sync failed'}. Please try again — the term planner is built from this data.`
          );
          return;
        } finally {
          setSyncingClubsFor(null);
        }
      }

      // Clubs are cached (or this child didn't want them) — safe to advance.
      await moveToNextChild();
    } catch (err: any) {
      setError(err.message || 'Failed to import events');
    } finally {
      setImporting(false);
    }
  };

  // Move to next child, waiting for background fetch if needed
  const moveToNextChild = async () => {
    if (currentChildIndex >= childrenWithSchools.length - 1) {
      setStep(5); // Done - all children processed
      return;
    }

    const nextChildIndex = currentChildIndex + 1;
    setCurrentChildIndex(nextChildIndex);
  };

  // Skip current child
  const handleSkipChild = () => {
    moveToNextChild();
  };

  const formatEventDate = (dateStr: string | null): string => {
    if (!dateStr) return 'Unknown date';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short'
      });
    } catch {
      return dateStr;
    }
  };

  const formatEventTime = (timeStr: string | null): string => {
    if (!timeStr) return 'All day';
    return timeStr.substring(0, 5);
  };

  const handleClose = (completed: boolean = false) => {
    setStep(1);
    setError(null);
    setChildSchoolMappings({});
    setChildManualUrls({});
    setShowManualUrlInput({});
    setSearchResults({});
    setSearching({});
    setSelectedSite({});
    setShowSearchExpanded({});
    setCredentials({});
    setShowPassword({});
    setSelectedYearGroups({});
    setExcludeSportFixtures({});
    setDeselectedEvents({});
    setAvailableYearGroupsPerSchool({});
    setPortalPupilsPerSchool({});
    setSiteUuidPerSchool({});
    setFetchedEventsPerSchool({});
    setCurrentChildIndex(0);
    setChildrenWithSchools([]);
    setChildImportStatus({});
    setSyncStatus('');
    setRetryStatus('');
    setIsRetrying(false);
    onClose(completed);
  };

  const getSchoolNameForUrl = (url: string): string => {
    for (const child of children) {
      if (childManualUrls[child.uuid]?.trim() === url) {
        return childSchoolMappings[child.uuid] || 'Unknown School';
      }
    }
    return url;
  };

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !mouseDownInsideModal.current) {
      handleClose(false);
    }
  }, []);

  const handleOverlayMouseDown = useCallback((e: React.MouseEvent) => {
    mouseDownInsideModal.current = e.target !== e.currentTarget;
  }, []);

  if (!isOpen) return null;

  const currentChild = childrenWithSchools[currentChildIndex];
  const currentSchoolUrl = currentChild ? childManualUrls[currentChild.uuid]?.trim() : '';
  const currentYearGroups = currentSchoolUrl ? availableYearGroupsPerSchool[currentSchoolUrl] || [] : [];
  const currentFilteredEvents = currentChild ? getFilteredEventsForChild(currentChild) : [];
  const currentEventsToImport = currentChild ? getEventsToImport(currentChild) : [];
  const currentDeselected = currentChild ? (deselectedEvents[currentChild.uuid] || new Set()) : new Set();
  const totalEventsForChild = currentSchoolUrl ? (fetchedEventsPerSchool[currentSchoolUrl]?.length || 0) : 0;

  return (
    <div
      className="socs-modal-overlay"
      onClick={handleOverlayClick}
      onMouseDown={handleOverlayMouseDown}
    >
      <div
        className="socs-modal"
        onMouseDown={(e) => {
          e.stopPropagation();
          mouseDownInsideModal.current = true;
        }}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: step === 4 ? '700px' : '600px' }}
      >
        <div className="socs-modal-header">
          <h2>🏫 My School Portal</h2>
          <button className="socs-modal-close" onClick={() => handleClose(false)}>×</button>
        </div>

        <div className="socs-modal-content" ref={modalContentRef}>
          {/* Step Indicator */}
          <div className="socs-steps">
            <div className={`socs-step ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`}>1. School</div>
            <div className={`socs-step ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`}>2. Login</div>
            <div className={`socs-step ${step === 3 ? 'active' : step > 3 ? 'completed' : ''}`}>3. Fetch</div>
            <div className={`socs-step ${step === 4 ? 'active' : step > 4 ? 'completed' : ''}`}>4. Import</div>
            <div className={`socs-step ${step === 5 ? 'active' : ''}`}>5. Done</div>
          </div>

          {error && <div className="socs-error">{error}</div>}

          {/* Step 1: School Search */}
          {step === 1 && (
            <div className="socs-step-content">
              {/* Only show instructions if no children have search results or selected sites */}
              {!children.some(c => (searchResults[c.uuid]?.length > 0) || selectedSite[c.uuid]) && (
                <>
                  <p>Enter the school name for each child and we'll search for their My School Portal website.</p>
                  <p className="socs-hint">Example: For "https://parents.berkhamsted.com" enter just "Berkhamsted"</p>
                  <p className="socs-hint" style={{ fontStyle: 'normal', color: '#888', marginTop: '8px' }}>
                    Note: Leave blank if the child's school doesn't have a My School Portal account.
                  </p>
                </>
              )}

              {children.filter(c => !c.is_parent).map(child => (
                <div key={child.uuid} className="socs-child-input">
                  <label>{(child.display_name || child.name)}'s School:</label>

                  {selectedSite[child.uuid] && !showSearchExpanded[child.uuid] && !searching[child.uuid] && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#d4edda',
                      border: '2px solid #28a745',
                      borderRadius: '8px',
                      marginTop: '8px'
                    }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#155724', marginBottom: '8px' }}>
                        ✓ School Portal Found
                      </div>
                      <a href={selectedSite[child.uuid]} target="_blank" rel="noopener noreferrer"
                        style={{
                          color: '#007bff',
                          fontSize: '13px',
                          wordBreak: 'break-all',
                          display: 'block',
                          marginBottom: '12px'
                        }}>
                        {selectedSite[child.uuid]}
                      </a>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button type="button"
                          onClick={() => setShowSearchExpanded(prev => ({ ...prev, [child.uuid]: true }))}
                          style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: 'transparent',
                            color: '#666', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}>
                          Not Your School?
                        </button>
                        <button type="button"
                          onClick={() => {
                            setSelectedSite(prev => ({ ...prev, [child.uuid]: '' }));
                            setChildManualUrls(prev => ({ ...prev, [child.uuid]: '' }));
                            setSearchResults(prev => ({ ...prev, [child.uuid]: [] }));
                            setShowSearchExpanded(prev => ({ ...prev, [child.uuid]: false }));
                            setShowManualUrlInput(prev => ({ ...prev, [child.uuid]: false }));
                          }}
                          style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: '#dc3545',
                            color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                          Clear
                        </button>
                      </div>
                    </div>
                  )}

                  {searching[child.uuid] && (
                    <div style={{ marginTop: '8px', padding: '16px', backgroundColor: '#e3f2fd',
                      border: '1px solid #90caf9', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '20px', height: '20px', border: '3px solid #90caf9',
                        borderTop: '3px solid #1976d2', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                      <span style={{ color: '#1565c0', fontSize: '14px', fontWeight: 500 }}>
                        Searching for "{childSchoolMappings[child.uuid]}" School Portal...
                      </span>
                    </div>
                  )}

                  {!searching[child.uuid] && (!selectedSite[child.uuid] || showSearchExpanded[child.uuid]) && (
                    <>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <input type="text" value={childSchoolMappings[child.uuid] || ''}
                          onChange={(e) => handleSchoolNameChange(child.uuid, e.target.value)}
                          placeholder="e.g. Berkhamsted" style={{ flex: 1 }} />
                        <button onClick={() => handleSearchSchoolSite(child.uuid)}
                          disabled={!childSchoolMappings[child.uuid]?.trim()}
                          style={{ padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none',
                            borderRadius: '4px', cursor: childSchoolMappings[child.uuid]?.trim() ? 'pointer' : 'not-allowed',
                            opacity: childSchoolMappings[child.uuid]?.trim() ? 1 : 0.5, whiteSpace: 'nowrap' }}>
                          🔍 Search
                        </button>
                      </div>

                      {searchResults[child.uuid]?.length > 0 && !searching[child.uuid] && (
                        <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f8f9fa',
                          border: '1px solid #dee2e6', borderRadius: '4px' }}>
                          <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 500 }}>
                            ✅ Found {searchResults[child.uuid].length} potential site(s):
                          </p>
                          {searchResults[child.uuid].map((site, index) => (
                            <div key={index} style={{ padding: '10px', marginBottom: '8px',
                              backgroundColor: selectedSite[child.uuid] === site.url ? '#d4edda' : 'white',
                              border: selectedSite[child.uuid] === site.url ? '2px solid #28a745' : '1px solid #ccc',
                              borderRadius: '4px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                  <a href={site.loginUrl || site.url} target="_blank" rel="noopener noreferrer"
                                    style={{ color: '#007bff', fontSize: '13px' }}>{site.url}</a>
                                  <div style={{ fontSize: '12px', color: '#666' }}>{site.title}</div>
                                </div>
                                <button onClick={() => {
                                  handleSelectSite(child.uuid, site.url);
                                  setShowSearchExpanded(prev => ({ ...prev, [child.uuid]: false }));
                                }}
                                style={{ padding: '4px 12px', backgroundColor: selectedSite[child.uuid] === site.url ? '#28a745' : '#007bff',
                                  color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                                  {selectedSite[child.uuid] === site.url ? '✓ Selected' : 'Select'}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {showManualUrlInput[child.uuid] && (
                    <div style={{ marginTop: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#ff6b6b' }}>
                        Manual URL (Required):
                      </label>
                      <input type="text" value={childManualUrls[child.uuid] || 'https://parents.'}
                        onChange={(e) => handleManualUrlChange(child.uuid, e.target.value)}
                        placeholder="e.g. https://parents.berkhamsted.com"
                        style={{ width: '100%', padding: '8px', border: '2px solid #ff6b6b', borderRadius: '4px' }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Step 2: Credentials */}
          {step === 2 && (
            <div className="socs-step-content">
              <p>Enter your login credentials for each school's My School Portal.</p>
              <p className="socs-hint" style={{ color: '#666' }}>
                Your credentials are securely encrypted and stored. They are only used to sync your calendar.
              </p>

              {hasClubsEnabledChild && (
                <div style={{
                  padding: '20px 22px',
                  marginBottom: '20px',
                  background: '#FFF3CD',
                  border: '2px solid #F5C518',
                  borderRadius: '8px',
                  color: '#5D4037',
                  fontSize: '16px',
                  lineHeight: '1.4',
                  boxShadow: '0 2px 6px rgba(245, 197, 24, 0.25)'
                }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '6px' }}>
                    ⚠️ Use the booking parent's login
                  </div>
                  Clubs only appear for the parent who actually booked them on My School Portal.
                </div>
              )}

              {Object.keys(credentials).map(schoolUrl => (
                <div key={schoolUrl} className="socs-child-section" style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>
                    🏫 {getSchoolNameForUrl(schoolUrl)}
                  </h3>
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>{schoolUrl}</p>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Email:</label>
                    {/* Show email selector if we have suggestions and not in custom input mode */}
                    {suggestedEmails.length > 0 && !showCustomEmailInput[schoolUrl] ? (
                      <div>
                        <select
                          value={credentials[schoolUrl]?.email || ''}
                          onChange={(e) => {
                            if (e.target.value === '__custom__') {
                              setShowCustomEmailInput(prev => ({ ...prev, [schoolUrl]: true }));
                              handleCredentialChange(schoolUrl, 'email', '');
                            } else {
                              handleCredentialChange(schoolUrl, 'email', e.target.value);
                            }
                          }}
                          style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white' }}>
                          <option value="">Select an email...</option>
                          {suggestedEmails.map((email, idx) => (
                            <option key={idx} value={email}>{email}</option>
                          ))}
                          <option value="__custom__">+ Add different email</option>
                        </select>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input type="email"
                          autoComplete="off"
                          name={`school-portal-email-${schoolUrl.replace(/[^a-z0-9]/gi, '')}`}
                          value={credentials[schoolUrl]?.email || ''}
                          onChange={(e) => handleCredentialChange(schoolUrl, 'email', e.target.value)}
                          placeholder="your.email@example.com"
                          style={{ flex: 1, padding: '10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }} />
                        {suggestedEmails.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowCustomEmailInput(prev => ({ ...prev, [schoolUrl]: false }))}
                            style={{ padding: '10px 12px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#f8f9fa', cursor: 'pointer', fontSize: '12px' }}>
                            ← Back
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Password:</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPassword[schoolUrl] ? 'text' : 'password'}
                        autoComplete="new-password"
                        name={`school-portal-pwd-${schoolUrl.replace(/[^a-z0-9]/gi, '')}`}
                        value={credentials[schoolUrl]?.password || ''}
                        onChange={(e) => handleCredentialChange(schoolUrl, 'password', e.target.value)}
                        placeholder="Enter your School Portal password"
                        style={{ width: '100%', padding: '10px', paddingRight: '40px', border: '1px solid #ccc',
                          borderRadius: '4px', fontSize: '14px' }} />
                      <button type="button"
                        onClick={() => setShowPassword(prev => ({ ...prev, [schoolUrl]: !prev[schoolUrl] }))}
                        style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>
                        {showPassword[schoolUrl] ? '🙈' : '👁️'}
                      </button>
                    </div>
                    <a href={schoolUrl} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '13px', color: '#1976d2', textDecoration: 'none', display: 'inline-block', marginTop: '6px' }}>
                      Forgot password? →
                    </a>
                  </div>
                </div>
              ))}

              {/* Fetch Events button - inline so user must scroll to see it */}
              <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
                <button
                  className="socs-btn socs-btn-primary"
                  onClick={handleStep2Next}
                  disabled={loading}
                  style={{ width: '100%', padding: '14px', fontSize: '16px' }}>
                  {loading ? 'Connecting...' : 'Fetch Events'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Fetching with Progress */}
          {step === 3 && (
            <div className="socs-step-content" style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ width: '48px', height: '48px', margin: '0 auto 20px',
                border: '4px solid #90caf9', borderTop: '4px solid #1976d2',
                borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              <h3>Connecting to School Portal...</h3>

              {/* Progress status message */}
              <div style={{
                margin: '20px auto',
                padding: '12px 20px',
                backgroundColor: '#e3f2fd',
                borderRadius: '8px',
                maxWidth: '350px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <p style={{
                  color: '#1565c0',
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  {syncStatus || 'Preparing to connect...'}
                </p>
              </div>

              <p style={{ fontSize: '12px', color: '#888', marginTop: '16px' }}>
                This may take up to 60 seconds. Please don't close this window.
              </p>

              {/* Progress indicator dots */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '8px',
                marginTop: '16px'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#1976d2',
                  animation: 'pulse 1.5s infinite',
                  animationDelay: '0s'
                }} />
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#1976d2',
                  animation: 'pulse 1.5s infinite',
                  animationDelay: '0.3s'
                }} />
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#1976d2',
                  animation: 'pulse 1.5s infinite',
                  animationDelay: '0.6s'
                }} />
              </div>
              <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @keyframes pulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
              `}</style>
            </div>
          )}

          {/* Step 4: Per-Child Import with Live Preview */}
          {step === 4 && currentChild && (() => {
            // Check if this child's school is still being fetched
            const currentChildSchoolUrl = childManualUrls[currentChild.uuid]?.trim();
            const isSchoolStillLoading = currentChildSchoolUrl && schoolsBeingFetched.has(currentChildSchoolUrl);
            const hasFetchFailed = currentChildSchoolUrl && schoolsFetchFailed.has(currentChildSchoolUrl);
            const currentChildEvents = currentChildSchoolUrl ? (fetchedEventsPerSchool[currentChildSchoolUrl] || []) : [];

            // Retry handler for this child's school
            const handleRetryFetch = async () => {
              if (!currentChildSchoolUrl) return;

              const schoolDomain = currentChildSchoolUrl.replace('https://', '').replace('http://', '').split('/')[0];

              // Mark as retrying — use isRetrying/retryStatus (not schoolsBeingFetched)
              // so we stay on the retry-specific UI instead of the shared loading
              // screen whose syncStatus gets overwritten by concurrent background fetches.
              setIsRetrying(true);
              setRetryStatus(`Waiting for other school to finish...`);

              // Wait for any background school fetches to complete before retrying.
              // Heroku can't run two Chromium instances concurrently — the second one
              // will OOM or timeout, causing the retry to always fail.
              while (schoolsBeingFetchedRef.current.size > 0) {
                await new Promise(r => setTimeout(r, 1000));
              }

              setRetryStatus(`Attempting login to ${schoolDomain}...`);

              const cred = credentials[currentChildSchoolUrl];
              if (!cred) {
                setRetryStatus('No credentials found');
                setIsRetrying(false);
                return;
              }

              try {
                // Find all children using this school URL
                const childrenForSchool = children.filter(child =>
                  childManualUrls[child.uuid]?.trim() === currentChildSchoolUrl
                );

                // Save the updated credentials first (in case user changed them)
                await apiService.schoolPortalSync(
                  currentChildSchoolUrl, cred.email, cred.password,
                  childrenForSchool.map(c => c.uuid), {}, true
                );

                setRetryStatus(`Login successful! Fetching events from ${schoolDomain}...`);

                // Fetch events
                const today = new Date();
                const oneYearAhead = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
                const fromDate = today.toISOString().split('T')[0];
                const toDate = oneYearAhead.toISOString().split('T')[0];

                const fetchResponse = await apiService.schoolPortalFetchEvents(
                  currentChildSchoolUrl, cred.email, cred.password, fromDate, toDate
                );

                if (fetchResponse.success) {
                  setRetryStatus(`Processing calendar data...`);
                  const { events, siteUuid, yearGroups, pupils } = processSchoolFetchResponse(currentChildSchoolUrl, fetchResponse);

                  if (events.length === 0) {
                    setRetryStatus(`No events found`);
                    setSchoolsFetchFailed(prev => new Set(prev).add(currentChildSchoolUrl));
                  } else {
                    setRetryStatus(`Found ${events.length} events!`);
                    setSiteUuidPerSchool(prev => ({ ...prev, [currentChildSchoolUrl]: siteUuid }));
                    setFetchedEventsPerSchool(prev => ({ ...prev, [currentChildSchoolUrl]: events }));
                    setAvailableYearGroupsPerSchool(prev => ({ ...prev, [currentChildSchoolUrl]: yearGroups }));
                    setPortalPupilsPerSchool(prev => ({ ...prev, [currentChildSchoolUrl]: pupils }));
                    setSchoolsFetchFailed(prev => {
                      const next = new Set(prev);
                      next.delete(currentChildSchoolUrl);
                      return next;
                    });
                  }
                } else {
                  setRetryStatus(`Failed to fetch events`);
                  setSchoolsFetchFailed(prev => new Set(prev).add(currentChildSchoolUrl));
                }
              } catch (err: any) {
                console.error('Retry failed:', err);
                const errorMsg = err?.message || '';
                if (errorMsg.includes('Login failed') || errorMsg.includes('authentication')) {
                  setRetryStatus(`Login failed - please check your credentials`);
                } else {
                  setRetryStatus(`Connection error - please try again`);
                }
                setSchoolsFetchFailed(prev => new Set(prev).add(currentChildSchoolUrl));
              } finally {
                setIsRetrying(false);
              }
            };

            if (isSchoolStillLoading) {
              // Show loading screen for this child while their school data is being fetched
              const schoolDomain = currentChildSchoolUrl?.replace('https://', '').replace('http://', '').split('/')[0];
              return (
                <div className="socs-step-content" style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', minHeight: '300px', padding: '40px 20px'
                }}>
                  <div style={{
                    width: '48px', height: '48px', marginBottom: '20px',
                    border: '4px solid #90caf9', borderTop: '4px solid #1976d2',
                    borderRadius: '50%', animation: 'spin 1s linear infinite'
                  }} />
                  <h3 style={{ margin: '0 0 12px 0', color: '#333' }}>Loading {currentChild.name}'s School Data</h3>

                  {/* Progress status */}
                  <div style={{
                    margin: '12px 0',
                    padding: '12px 20px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '8px',
                    maxWidth: '350px'
                  }}>
                    <p style={{ color: '#1565c0', margin: 0, fontSize: '14px', fontWeight: 500, textAlign: 'center' }}>
                      {syncStatus || `Connecting to ${schoolDomain}...`}
                    </p>
                  </div>

                  <p style={{ color: '#888', fontSize: '13px', marginTop: '16px' }}>
                    This may take up to 60 seconds
                  </p>

                  {/* Animated dots */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px' }}>
                    <div style={{
                      width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#1976d2',
                      animation: 'pulse 1.5s infinite', animationDelay: '0s'
                    }} />
                    <div style={{
                      width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#1976d2',
                      animation: 'pulse 1.5s infinite', animationDelay: '0.3s'
                    }} />
                    <div style={{
                      width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#1976d2',
                      animation: 'pulse 1.5s infinite', animationDelay: '0.6s'
                    }} />
                  </div>
                </div>
              );
            }

            // Show error state if fetch failed or returned no events
            if (hasFetchFailed || currentChildEvents.length === 0) {
              // Get the credentials used for this school
              const schoolCreds = currentChildSchoolUrl ? credentials[currentChildSchoolUrl] : null;

              return (
                <div className="socs-step-content" style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', minHeight: '300px', padding: '40px 20px'
                }}>
                  {isRetrying ? (
                    // Show spinner and status while retrying
                    <>
                      <div style={{
                        width: '48px', height: '48px', marginBottom: '20px',
                        border: '4px solid #90caf9', borderTop: '4px solid #1976d2',
                        borderRadius: '50%', animation: 'spin 1s linear infinite'
                      }} />
                      <h3 style={{ margin: '0 0 12px 0', color: '#1565c0' }}>Retrying Connection...</h3>
                      <div style={{
                        margin: '12px 0',
                        padding: '12px 20px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '8px',
                        maxWidth: '350px'
                      }}>
                        <p style={{ color: '#1565c0', margin: 0, fontSize: '14px', fontWeight: 500 }}>
                          {retryStatus || 'Connecting...'}
                        </p>
                      </div>
                      <p style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
                        Please wait...
                      </p>
                    </>
                  ) : (
                    // Show error state with editable credentials
                    <>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                      <h3 style={{ margin: '0 0 12px 0', color: '#856404' }}>Unable to Import Events for {currentChild.name}</h3>
                      <p style={{ color: '#666', textAlign: 'center', margin: '0 0 16px 0', maxWidth: '350px' }}>
                        We couldn't retrieve any events from the school portal. Please check your login details and try again.
                      </p>

                      {/* Show last retry status if there was one */}
                      {retryStatus && (
                        <div style={{
                          margin: '0 0 16px 0',
                          padding: '10px 16px',
                          backgroundColor: '#fff3cd',
                          border: '1px solid #ffc107',
                          borderRadius: '6px',
                          maxWidth: '350px'
                        }}>
                          <p style={{ color: '#856404', margin: 0, fontSize: '13px' }}>
                            ⚠️ {retryStatus}
                          </p>
                        </div>
                      )}

                      {/* Editable credentials */}
                      {currentChildSchoolUrl && (
                        <div style={{
                          backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px',
                          padding: '16px', margin: '0 0 20px 0', width: '100%', maxWidth: '350px'
                        }}>
                          <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#495057' }}>
                            Login details for {currentChildSchoolUrl.replace('https://', '').replace('http://', '').split('/')[0]}:
                          </p>
                          <p style={{ margin: '0 0 12px 0', fontSize: '11px', color: '#6c757d' }}>
                            ⚠️ Use your <strong>School Portal</strong> password, not your Cluubz password
                          </p>
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Email</label>
                            <input
                              type="email"
                              autoComplete="username"
                              value={schoolCreds?.email || ''}
                              onChange={(e) => setCredentials(prev => ({
                                ...prev,
                                [currentChildSchoolUrl]: { ...prev[currentChildSchoolUrl], email: e.target.value }
                              }))}
                              style={{
                                width: '100%', padding: '8px 12px', border: '1px solid #ced4da',
                                borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box'
                              }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Password</label>
                            <input
                              type="password"
                              autoComplete="current-password"
                              value={schoolCreds?.password || ''}
                              onChange={(e) => setCredentials(prev => ({
                                ...prev,
                                [currentChildSchoolUrl]: { ...prev[currentChildSchoolUrl], password: e.target.value }
                              }))}
                              placeholder="Re-enter your School Portal password"
                              style={{
                                width: '100%', padding: '8px 12px', border: '1px solid #ced4da',
                                borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box'
                              }}
                            />
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                          onClick={handleRetryFetch}
                          style={{
                            padding: '10px 24px', backgroundColor: '#1976d2', color: 'white',
                            border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 500
                          }}>
                          🔄 Try Again
                        </button>
                        <button
                          onClick={handleSkipChild}
                          style={{
                            padding: '10px 24px', backgroundColor: '#6c757d', color: 'white',
                            border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 500
                          }}>
                          Skip {currentChild.name}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            }

            return (
            <div className="socs-step-content" style={{ position: 'relative' }}>
              {/* Child Header with Progress */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '32px' }}>👤</span>
                  <div>
                    <h3 style={{ margin: 0 }}>{currentChild.name}</h3>
                    <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                      {totalEventsForChild} events from school
                    </p>
                  </div>
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  Child {currentChildIndex + 1} of {childrenWithSchools.length}
                </div>
              </div>

              {/* Year group: read from the school portal, not chosen by the parent.
                  The picker below is only a fallback for portals that don't report it. */}
              {detectedYearGroupFor(currentChild) ? (
                <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#d4edda',
                  borderRadius: '8px', border: '1px solid #28a745' }}>
                  <div style={{ fontWeight: 600, color: '#155724', fontSize: '14px' }}>
                    🎓 {currentChild.name} is in {detectedYearGroupFor(currentChild)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#155724', marginTop: '4px' }}>
                    Taken from the school portal and kept up to date automatically,
                    including when they move up a year.
                  </div>
                </div>
              ) : (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                  Filter by Year Group (select multiple):
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {currentYearGroups.length === 0 ? (
                    <p style={{ fontSize: '13px', color: '#888', fontStyle: 'italic' }}>
                      No year groups found - all events will be shown
                    </p>
                  ) : (
                    currentYearGroups.map(yg => {
                      const isSelected = selectedYearGroups[currentChild.uuid]?.has(yg);
                      return (
                        <button key={yg} type="button"
                          onClick={() => toggleYearGroup(currentChild.uuid, yg)}
                          style={{
                            padding: '6px 14px',
                            fontSize: '13px',
                            border: isSelected ? '2px solid #28a745' : '1px solid #ccc',
                            borderRadius: '20px',
                            backgroundColor: isSelected ? '#d4edda' : 'white',
                            color: isSelected ? '#155724' : '#333',
                            cursor: 'pointer',
                            fontWeight: isSelected ? 600 : 400
                          }}>
                          {isSelected && '✓ '}{yg}
                        </button>
                      );
                    })
                  )}
                </div>
                {selectedYearGroups[currentChild.uuid]?.size === 0 && currentYearGroups.length > 0 && (
                  <p style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
                    No year groups selected - showing all events
                  </p>
                )}
              </div>
              )}

              {/* Sport Fixture Toggle */}
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff3cd',
                borderRadius: '8px', border: '1px solid #ffc107' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={excludeSportFixtures[currentChild.uuid] || false}
                    onChange={(e) => setExcludeSportFixtures(prev => ({ ...prev, [currentChild.uuid]: e.target.checked }))}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                  <span style={{ fontWeight: 500 }}>Exclude sport fixtures</span>
                </label>
              </div>

              {/* Live Event Preview */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 12px', backgroundColor: '#f8f9fa', borderRadius: '8px 8px 0 0', border: '1px solid #ddd' }}>
                  <span style={{ fontWeight: 500 }}>
                    {currentEventsToImport.length} events to import
                    {currentDeselected.size > 0 && (
                      <span style={{ color: '#666', fontWeight: 400 }}> ({currentDeselected.size} deselected)</span>
                    )}
                  </span>
                  <span style={{ fontSize: '13px', color: '#666' }}>
                    (future events only)
                  </span>
                </div>
                <div style={{ border: '1px solid #ddd',
                  borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
                  {currentFilteredEvents.length === 0 ? (
                    <div style={{ padding: '30px', textAlign: 'center', color: '#666' }}>
                      <span style={{ fontSize: '32px', display: 'block', marginBottom: '8px' }}>📭</span>
                      No events match your filters
                    </div>
                  ) : (
                    currentFilteredEvents.slice(0, 50).map((event, index) => {
                      const isDeselected = currentDeselected.has(event.uuid);
                      return (
                        <div key={event.uuid} style={{ padding: '10px 12px',
                          borderBottom: index < Math.min(currentFilteredEvents.length, 50) - 1 ? '1px solid #eee' : 'none',
                          backgroundColor: isDeselected ? '#f8f9fa' : 'white',
                          opacity: isDeselected ? 0.6 : 1 }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <input
                              type="checkbox"
                              checked={!isDeselected}
                              onChange={() => toggleEventSelection(currentChild.uuid, event.uuid)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 500, fontSize: '14px', marginBottom: '4px',
                                textDecoration: isDeselected ? 'line-through' : 'none' }}>
                                {event.title}
                              </div>
                              <div style={{ fontSize: '12px', color: '#666', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <span>📅 {formatEventDate(event.event_date)}</span>
                                <span>🕐 {formatEventTime(event.start_time)}</span>
                                {event.venue && <span>📍 {event.venue}</span>}
                              </div>
                              {event.event_type === 'Fixture' && (
                                <span style={{ display: 'inline-block', marginTop: '4px', padding: '2px 8px',
                                  fontSize: '11px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '4px' }}>
                                  Sport Fixture
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  {currentFilteredEvents.length > 50 && (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#666', fontSize: '13px',
                      borderTop: '1px solid #eee' }}>
                      ... and {currentFilteredEvents.length - 50} more events
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })()}

          {/* Step 5: Complete */}
          {step === 5 && (
            <div className="socs-step-content" style={{ textAlign: 'center', padding: '30px 20px' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
              <h3>Import Complete!</h3>

              <div style={{ marginTop: '20px', textAlign: 'left' }}>
                {Object.entries(childImportStatus).map(([childUuid, status]) => {
                  const child = children.find(c => c.uuid === childUuid);
                  return (
                    <div key={childUuid} style={{ padding: '12px', marginBottom: '8px',
                      backgroundColor: '#d4edda', borderRadius: '8px' }}>
                      <div style={{ fontWeight: 500 }}>👤 {child?.name}</div>
                      <div style={{ fontSize: '13px', color: '#155724' }}>
                        ✅ {status.imported} events imported
                        {status.skipped > 0 && ` (${status.skipped} already in calendar)`}
                      </div>
                    </div>
                  );
                })}
                {Object.keys(childImportStatus).length === 0 && (
                  <p style={{ textAlign: 'center', color: '#666' }}>No events were imported.</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="socs-modal-footer">
          {/* Step 1: Cancel / Next */}
          {step === 1 && (
            <>
              <button className="socs-btn socs-btn-secondary" onClick={() => handleClose(false)}>
                Cancel
              </button>
              {children.some(child => childManualUrls[child.uuid]?.trim() && childManualUrls[child.uuid].trim() !== 'https://parents.') && (
                <button className="socs-btn socs-btn-primary" onClick={handleStep1Next}>
                  Next: Enter Credentials
                </button>
              )}
            </>
          )}

          {/* Step 2: Back only - Fetch Events button is inline in content */}
          {step === 2 && (
            <button className="socs-btn socs-btn-secondary" onClick={() => setStep(1)} disabled={loading}>
              Back
            </button>
          )}

          {/* Step 4: Skip / Import */}
          {step === 4 && currentChild && (() => {
            const currentChildSchoolUrl = childManualUrls[currentChild.uuid]?.trim();
            const isSchoolStillLoading = currentChildSchoolUrl && schoolsBeingFetched.has(currentChildSchoolUrl);

            // Don't show buttons while school data is still loading
            if (isSchoolStillLoading) return null;

            return (
              <>
                <button className="socs-btn socs-btn-secondary" onClick={handleSkipChild} disabled={importing}>
                  Skip {currentChild.name}
                </button>
                <button className="socs-btn socs-btn-primary" onClick={handleImportForChild}
                  disabled={importing || currentEventsToImport.length === 0}>
                  {syncingClubsFor
                    ? `Importing ${syncingClubsFor}'s clubs...`
                    : importing
                      ? 'Importing...'
                      : `Import ${currentEventsToImport.length} Events`}
                </button>
              </>
            );
          })()}

          {/* Step 5: Done */}
          {step === 5 && (
            <button className="socs-btn socs-btn-primary" onClick={() => handleClose(true)}>
              Done
            </button>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default SchoolPortalSetupModal;
